import { useState } from "react";
import { formatDate } from "@/lib/i18n";

interface PropertyAssetTabProps {
  contactId: string;
}

interface Property {
  id: string;
  address: string;
  type: string;
  assets: Asset[];
}

interface Asset {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  installDate: string;
  warrantyExpiry: string | null;
  condition: "good" | "fair" | "poor" | "needs_replacement";
  lastServiceDate: string | null;
}

/**
 * E.9: Property / Asset Management Tab
 * Per-contact properties with asset inventory, service history, warranty alerts.
 * Data loaded from service delivery engine via API.
 */
export function PropertyAssetTab({ contactId: _contactId }: PropertyAssetTabProps) {
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);

  // Placeholder — will connect to service-delivery API
  const properties: Property[] = [];
  const isLoading = false;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">No properties registered for this contact.</p>
        <button className="mt-3 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
          Add Property
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Properties ({properties.length})</h3>
        <button className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground">
          Add Property
        </button>
      </div>

      {properties.map((property) => (
        <div key={property.id} className="rounded-lg border border-border">
          <button
            onClick={() =>
              setExpandedProperty(expandedProperty === property.id ? null : property.id)
            }
            className="flex w-full items-center justify-between p-3 text-left text-sm"
          >
            <div>
              <p className="font-medium">{property.address}</p>
              <p className="text-xs text-muted-foreground">{property.type}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {property.assets.length} assets
              </span>
              <span className="text-muted-foreground">
                {expandedProperty === property.id ? "▲" : "▼"}
              </span>
            </div>
          </button>

          {expandedProperty === property.id && (
            <div className="border-t border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  Asset Inventory
                </p>
                <button className="text-xs text-primary hover:underline">Add Asset</button>
              </div>

              {property.assets.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  No assets registered.
                </p>
              ) : (
                <div className="space-y-2">
                  {property.assets.map((asset) => (
                    <AssetCard key={asset.id} asset={asset} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function AssetCard({ asset }: { asset: Asset }) {
  const isWarrantyExpiring =
    asset.warrantyExpiry &&
    new Date(asset.warrantyExpiry).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;

  const conditionColors: Record<string, string> = {
    good: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    fair: "bg-[var(--color-warning)]/10 text-yellow-800",
    poor: "bg-[var(--color-accent-action-light)] text-orange-800",
    needs_replacement: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  };

  return (
    <div className="rounded-md border border-border p-2.5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium">{asset.name}</p>
          <p className="text-xs text-muted-foreground">
            {asset.manufacturer} {asset.model}
          </p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${conditionColors[asset.condition] ?? "bg-muted text-muted-foreground"}`}
        >
          {asset.condition.replace("_", " ")}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
        <span className="text-muted-foreground">
          Installed: {formatDate(asset.installDate)}
        </span>
        {asset.lastServiceDate && (
          <span className="text-muted-foreground">
            Last service: {formatDate(asset.lastServiceDate)}
          </span>
        )}
      </div>

      {isWarrantyExpiring && asset.warrantyExpiry && (
        <div className="mt-2 rounded-md bg-[var(--color-accent-action-light)] p-2 text-xs text-orange-700">
          Warranty expires {formatDate(asset.warrantyExpiry)}
        </div>
      )}
    </div>
  );
}
